/**
 * Donation form product picker.
 *
 * Paired with templates/_molecules/donationProducts.twig, which Formie renders inside
 * the donation form's `productsList` HTML field.
 */
import { writeField } from './donation-formie-fields'
;(() => {
    const CONTAINER = '.donation-products'
    const ITEM = '.donation-product-item'
    const TOGGLE = '.donation-product__toggle'
    const TITLE = '.donation-product__title'
    const SUMMARY = '.donation-products__summary'
    const TOTAL = '.donation-products__total'

    const QTY_INPUT = '.donation-product__qty'
    const QTY_DOWN = '.donation-product__qty-down'
    const QTY_UP = '.donation-product__qty-up'
    const LINE_TOTAL = '.donation-product__line-total'

    // Added to the `.donation-product-item` of each selected product.
    const SELECTED_CLASS = 'donation-product-item--selected'

    const FIELD_AMOUNT = 'productsPurchasedAmount'
    const FIELD_LIST = 'productsPurchased'

    // 0 is a valid quantity and means "not selected". There is no upper bound.
    const MIN_QTY = 0

    const formatCents = (cents) =>
        (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })

    /**
     * Renders the selection for `productsPurchased` as CSV-quoted values with the quantity appended: "Badge x2","Cookbook x1"
     */
    const formatTitles = (entries) =>
        entries.map(({ title, qty }) => `"${`${title} x${qty}`.replace(/"/g, '""')}"`).join(',')

    /**
     * Reads the quantity for an item, down to a whole number of at least MIN_QTY.
     */
    const readQty = (item) => {
        const input = item?.querySelector(QTY_INPUT)

        if (!input) {
            return MIN_QTY
        }

        const parsed = Math.floor(Number(input.value))

        return Number.isFinite(parsed) && parsed >= MIN_QTY ? parsed : MIN_QTY
    }

    /**
     * Writes the quantity back without firing a `change` event, so this can be called from inside the handler that is already syncing.
     */
    const writeQty = (item, qty) => {
        const input = item?.querySelector(QTY_INPUT)

        if (input && input.value !== String(qty)) {
            input.value = String(qty)
        }
    }

    const sync = (container) => {
        const form = container.closest('form')

        if (!form) {
            return
        }

        const items = Array.from(container.querySelectorAll(ITEM))

        let cents = 0
        const selected = []

        items.forEach((item) => {
            const qty = readQty(item)
            const isSelected = qty > 0

            writeQty(item, qty)

            // The checkbox is state, not a control — it drives `has-[:checked]:` styling and carries the unit price.
            const toggle = item.querySelector(TOGGLE)

            if (toggle) {
                toggle.checked = isSelected
            }

            // Mark the selected items so they can be styled and queried from elsewhere.
            item.classList.toggle(SELECTED_CLASS, isSelected)

            const down = item.querySelector(QTY_DOWN)

            if (down) {
                down.disabled = qty <= MIN_QTY
            }

            const unitCents = parseInt(toggle?.value, 10) || 0
            const lineCents = unitCents * qty

            cents += lineCents

            // Only worth showing once it differs from the unit price beside it.
            const lineTotal = item.querySelector(LINE_TOTAL)

            if (lineTotal) {
                lineTotal.textContent = qty > 1 ? formatCents(lineCents) : ''
                lineTotal.hidden = qty <= 1
            }

            const title = item.querySelector(TITLE)?.textContent.trim()

            if (isSelected && title) {
                selected.push({ title, qty })
            }
        })

        // Write '0.00', never '' — an empty amount makes the `totalAmount` calculation evaluate to empty
        writeField(form, FIELD_AMOUNT, (cents / 100).toFixed(2), 'donationProducts')
        writeField(form, FIELD_LIST, formatTitles(selected), 'donationProducts')

        const summary = container.querySelector(SUMMARY)
        const total = container.querySelector(TOTAL)

        if (total) {
            total.textContent = formatCents(cents)
        }

        if (summary) {
            summary.hidden = cents === 0
        }
    }

    /**
     * Nudges the quantity by `delta`.
     */
    const step = (container, item, delta) => {
        writeQty(item, Math.max(MIN_QTY, readQty(item) + delta))

        sync(container)
    }

    const init = () => {
        document.querySelectorAll(CONTAINER).forEach((container) => {
            if (container.dataset.donationProductsReady) {
                return
            }

            container.dataset.donationProductsReady = 'true'

            container.addEventListener('change', (event) => {
                if (event.target.matches(QTY_INPUT)) {
                    sync(container)
                }
            })

            // Typing in the quantity box updates the total without waiting for blur.
            container.addEventListener('input', (event) => {
                if (event.target.matches(QTY_INPUT)) {
                    sync(container)
                }
            })

            container.addEventListener('click', (event) => {
                const down = event.target.closest(QTY_DOWN)
                const up = event.target.closest(QTY_UP)

                if (!down && !up) {
                    return
                }

                const item = event.target.closest(ITEM)

                if (item) {
                    step(container, item, down ? -1 : 1)
                }
            })

            // Enter in a number input submits the form in most browsers, which on a
            // multi-page Formie form advances the page mid-selection.
            container.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' && event.target.matches(QTY_INPUT)) {
                    event.preventDefault()
                }
            })

            // Sync once on load
            sync(container)
        })
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init)
    } else {
        init()
    }
})()
